import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { FileDropzone } from '@/components/FileDropzone'
import { EmptyState, ErrorMessage, PageLoader, Spinner } from '@/components/ui'
import { api, uploadFile } from '@/lib/api'
import { formatDateTime, formatNumber } from '@/lib/format'
import {
  RUN_STATUS_TITLES,
  type DatasetListResponse,
  type ImportAccepted,
  type RefreshRun,
  type RefreshRunListResponse,
} from '@/lib/types'
import { useActiveWorkspace, useHasPermission } from '@/stores/auth'

/** Как часто опрашивается статус обработки, пока файл не готов. */
const POLL_INTERVAL_MS = 1500

export function SourcesPage() {
  const workspace = useActiveWorkspace()
  const canManage = useHasPermission('source:manage')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const workspaceId = workspace?.workspace_id

  const datasets = useQuery({
    queryKey: ['datasets', workspaceId],
    queryFn: () => api.get<DatasetListResponse>('/datasets'),
    enabled: Boolean(workspaceId),
  })

  const imports = useQuery({
    queryKey: ['imports', workspaceId],
    queryFn: () => api.get<RefreshRunListResponse>('/imports?limit=10'),
    enabled: Boolean(workspaceId),
  })

  // Пока файл обрабатывается, статус опрашивается: пользователь должен видеть
  // прогресс, а не пустой экран с неизвестным исходом
  const activeRun = useQuery({
    queryKey: ['import-run', activeRunId],
    queryFn: () => api.get<RefreshRun>(`/imports/${activeRunId}`),
    enabled: Boolean(activeRunId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'completed' || status === 'failed' ? false : POLL_INTERVAL_MS
    },
  })

  // Список наборов обновляется по завершении обработки, а не только в момент
  // приёма файла: пока файл разбирается, у набора нет текущей версии, и без
  // повторного запроса кнопка «Профиль данных» в списке не появилась бы
  const finishedRunId =
    activeRun.data?.status === 'completed' || activeRun.data?.status === 'failed'
      ? activeRun.data.id
      : null

  useEffect(() => {
    if (!finishedRunId) return
    void queryClient.invalidateQueries({ queryKey: ['datasets', workspaceId] })
    void queryClient.invalidateQueries({ queryKey: ['imports', workspaceId] })
  }, [finishedRunId, queryClient, workspaceId])

  function handleAccepted(result: ImportAccepted): void {
    setActiveRunId(result.refresh_run_id)
    setNotice(result.message)
    void queryClient.invalidateQueries({ queryKey: ['datasets', workspaceId] })
    void queryClient.invalidateQueries({ queryKey: ['imports', workspaceId] })
  }

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      // Ключ идемпотентности защищает от повторной отправки: двойной клик
      // не должен создавать две версии набора данных
      return uploadFile<ImportAccepted>('/imports', form, {
        idempotencyKey: `upload-${file.name}-${file.size}-${file.lastModified}`,
      })
    },
    onSuccess: handleAccepted,
  })

  const demo = useMutation({
    mutationFn: () => api.post<ImportAccepted>('/imports/demo'),
    onSuccess: handleAccepted,
  })

  const runFinished = activeRun.data?.status === 'completed' || activeRun.data?.status === 'failed'

  if (!workspace) return <PageLoader />

  return (
    <div className="space-y-6">
      {canManage && (
        <section className="card p-6">
          <h2 className="text-base font-semibold text-slate-900">Загрузить данные</h2>
          <p className="mt-1 text-sm text-slate-500">
            Система сама разберёт структуру файла: найдёт заголовки, определит типы колонок,
            исключит строки «Итого» и покажет проблемы качества данных.
          </p>

          <div className="mt-4">
            <FileDropzone
              onSelect={(file) => upload.mutate(file)}
              isUploading={upload.isPending}
              disabled={demo.isPending}
            />
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <span className="text-sm text-slate-500">Нет файла под рукой?</span>
            <button
              type="button"
              className="btn-secondary"
              disabled={demo.isPending || upload.isPending}
              onClick={() => demo.mutate()}
            >
              {demo.isPending && <Spinner className="h-4 w-4" />}
              Попробовать на примере
            </button>
            <span className="text-xs text-slate-400">
              Создадим демо-выгрузку продаж за 18 месяцев
            </span>
          </div>

          <div className="mt-3">
            <ErrorMessage error={upload.error ?? demo.error} />
          </div>
        </section>
      )}

      {activeRunId && activeRun.data && (
        <ImportProgress
          run={activeRun.data}
          notice={notice}
          onOpen={() => {
            if (activeRun.data?.dataset_version_id) {
              void navigate(`/dataset-versions/${activeRun.data.dataset_version_id}`)
            }
          }}
          onDismiss={() => {
            setActiveRunId(null)
            setNotice(null)
            void queryClient.invalidateQueries({ queryKey: ['datasets', workspaceId] })
          }}
          finished={runFinished}
        />
      )}

      <section className="card overflow-hidden">
        <header className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            Наборы данных{datasets.data ? ` · ${datasets.data.total}` : ''}
          </h2>
        </header>

        {datasets.isLoading && <PageLoader />}
        <div className="px-6">
          <ErrorMessage error={datasets.error} />
        </div>

        {datasets.data?.items.length === 0 && (
          <div className="p-6">
            <EmptyState
              title="Данных пока нет"
              description="Загрузите файл или создайте демо-набор — дальше система предложит виджеты по вашей бизнес-цели."
            />
          </div>
        )}

        {datasets.data && datasets.data.items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {datasets.data.items.map((dataset) => (
              <li
                key={dataset.id}
                className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{dataset.name}</p>
                  <p className="tabular text-xs text-slate-500">
                    {formatNumber(dataset.row_count)} строк · версий: {dataset.version_count} ·
                    обновлён {formatDateTime(dataset.updated_at)}
                  </p>
                  {/* Пока сопоставление не подтверждено, по набору нельзя
                      построить ни один отчёт — об этом нужно сказать в списке,
                      а не оставлять пользователя выяснять это самому */}
                  {dataset.current_version_id && !dataset.mapping_confirmed && (
                    <p className="mt-1 text-xs text-amber-700">
                      Смысл колонок не подтверждён — отчёты пока недоступны
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  {dataset.current_version_id && !dataset.mapping_confirmed && (
                    <Link
                      to={`/dataset-versions/${dataset.current_version_id}/mapping`}
                      className="btn-primary py-1.5 text-xs"
                    >
                      Указать смысл колонок
                    </Link>
                  )}
                  {dataset.current_version_id && (
                    <Link
                      to={`/dataset-versions/${dataset.current_version_id}`}
                      className="btn-secondary py-1.5 text-xs"
                    >
                      Профиль данных
                    </Link>
                  )}
                  <Link
                    to={`/datasets/${dataset.id}/versions`}
                    className="btn-ghost py-1.5 text-xs"
                  >
                    История версий
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card overflow-hidden">
        <header className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">История импортов</h2>
        </header>

        <div className="px-6">
          <ErrorMessage error={imports.error} />
        </div>

        {imports.data?.items.length === 0 && (
          <p className="px-6 py-4 text-sm text-slate-500">Импортов ещё не было.</p>
        )}

        {imports.data && imports.data.items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {imports.data.items.map((run) => (
              <li key={run.id} className="flex items-center justify-between gap-3 px-6 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-900">
                    {run.file_name ?? 'без имени файла'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatDateTime(run.created_at)}
                    {run.stats.rows ? ` · ${formatNumber(run.stats.rows)} строк` : ''}
                    {run.duration_seconds !== null ? ` · ${run.duration_seconds.toFixed(1)} с` : ''}
                  </p>
                  {run.error_message && (
                    <p className="mt-1 text-xs text-red-700">{run.error_message}</p>
                  )}
                </div>
                <RunStatusBadge status={run.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function RunStatusBadge({ status }: { status: RefreshRun['status'] }) {
  const styles: Record<RefreshRun['status'], string> = {
    queued: 'bg-slate-100 text-slate-600',
    running: 'bg-brand-100 text-brand-800',
    completed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
  }
  return <span className={`badge shrink-0 ${styles[status]}`}>{RUN_STATUS_TITLES[status]}</span>
}

/**
 * Прогресс обработки файла.
 *
 * Показывает конкретный следующий шаг: при успехе — открыть профиль данных,
 * при ошибке — что именно не так с файлом. «Ошибка импорта» без объяснения
 * приводит пользователя в поддержку.
 */
function ImportProgress({
  run,
  notice,
  finished,
  onOpen,
  onDismiss,
}: {
  run: RefreshRun
  notice: string | null
  finished: boolean
  onOpen: () => void
  onDismiss: () => void
}) {
  const isFailed = run.status === 'failed'

  return (
    <section
      className={`card p-6 ${isFailed ? 'border-red-200 bg-red-50' : finished ? 'border-emerald-200 bg-emerald-50' : ''}`}
    >
      <div className="flex items-start gap-3">
        {!finished && <Spinner className="mt-0.5 h-5 w-5 text-brand-600" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">
            {isFailed
              ? 'Файл не удалось обработать'
              : finished
                ? 'Файл обработан'
                : 'Обрабатываем файл…'}
          </p>

          {!finished && notice && <p className="mt-1 text-sm text-slate-600">{notice}</p>}

          {isFailed && (
            <p className="mt-1 text-sm text-red-800">
              {run.error_message ?? 'Причина неизвестна. Проверьте формат файла.'}
            </p>
          )}

          {finished && !isFailed && (
            <p className="tabular mt-1 text-sm text-slate-600">
              Загружено {formatNumber(run.stats.rows ?? 0)} строк, колонок {run.stats.columns ?? 0}
              {run.stats.skipped_service_rows
                ? `, исключено итоговых строк: ${run.stats.skipped_service_rows}`
                : ''}
              {run.stats.issues ? `, замечаний к данным: ${run.stats.issues}` : ''}.
            </p>
          )}

          {finished && (
            <div className="mt-3 flex gap-2">
              {!isFailed && (
                <button type="button" className="btn-primary py-1.5 text-xs" onClick={onOpen}>
                  Открыть профиль данных
                </button>
              )}
              <button type="button" className="btn-secondary py-1.5 text-xs" onClick={onDismiss}>
                Закрыть
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
